import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Star, MessageSquare } from 'lucide-react';
import { Button } from '../ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ReviewCard from './ReviewCard';
import WriteReview from './WriteReview';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProductReviews = ({ productId, productName }) => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    averageRating: 0,
    totalReviews: 0,
    ratingDistribution: {}
  });
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [sortBy, setSortBy] = useState('recent');
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('accessToken');
    setIsAuthenticated(!!token);
    loadReviews();
  }, [productId, page, sortBy]);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${API}/reviews/product/${productId}?page=${page}&limit=10&sort=${sortBy}`
      );
      
      setReviews(response.data.reviews || []);
      setStats({
        averageRating: response.data.averageRating || 0,
        totalReviews: response.data.totalReviews || 0,
        ratingDistribution: response.data.ratingDistribution || {}
      });
      setTotalPages(response.data.pages || 1);
    } catch (error) {
      console.error('Load reviews error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleHelpful = async (reviewId, helpful) => {
    if (!isAuthenticated) {
      toast({
        title: t('productReviews.loginRequired'),
        description: t('productReviews.loginRequiredDesc'),
        variant: 'destructive'
      });
      return;
    }

    try {
      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API}/reviews/${reviewId}/helpful`,
        { helpful },
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { helpful }
        }
      );
      
      // Reload reviews to get updated counts
      loadReviews();
      
      toast({
        title: t('productReviews.voteThanks'),
      });
    } catch (error) {
      console.error('Vote error:', error);
      const errorMsg = error.response?.data?.detail || t('productReviews.voteFailed');
      toast({
        title: t('common.error'),
        description: errorMsg,
        variant: 'destructive'
      });
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <Star
        key={i}
        className={`w-5 h-5 ${
          i < Math.floor(rating)
            ? 'fill-yellow-400 text-yellow-400'
            : 'text-gray-300'
        }`}
      />
    ));
  };

  const renderRatingBar = (stars) => {
    const count = stats.ratingDistribution[stars] || 0;
    const percentage = stats.totalReviews > 0 ? (count / stats.totalReviews) * 100 : 0;
    const starLabel = stars === 1 ? t('productReviews.starLabel') : t('productReviews.starLabelPlural');

    return (
      <div key={`bar-${stars}`} className="flex items-center gap-2">
        <span className="text-sm text-gray-600 w-16">{stars} {starLabel}</span>
        <div className="flex-1 bg-gray-200 rounded-full h-2">
          <div
            className="bg-yellow-400 h-2 rounded-full transition-all"
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-sm text-gray-600 w-8 text-right">{count}</span>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Rating Summary */}
      <div className="bg-gray-50 rounded-lg p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col items-center justify-center">
            <div className="text-5xl font-bold text-gray-900 mb-2">
              {stats.averageRating.toFixed(1)}
            </div>
            <div className="flex items-center gap-1 mb-2">
              {renderStars(stats.averageRating)}
            </div>
            <div className="text-sm text-gray-600">
              {stats.totalReviews === 1
                ? t('productReviews.reviewsCount', { count: stats.totalReviews })
                : t('productReviews.reviewsCountPlural', { count: stats.totalReviews })}
            </div>
          </div>
          
          <div className="space-y-2">
            {[5, 4, 3, 2, 1].map((stars) => renderRatingBar(stars))}
          </div>
        </div>
        
        {isAuthenticated && (
          <div className="mt-6 pt-6 border-t border-gray-200">
            <Button
              onClick={() => setShowWriteReview(true)}
              className="bg-[#be1f1a] hover:bg-[#a71b17]"
            >
              <MessageSquare className="w-4 h-4 mr-2" />
              {t('productReviews.writeReview')}
            </Button>
          </div>
        )}
      </div>

      {/* Reviews List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-semibold text-gray-900">
            {t('productReviews.title')} ({stats.totalReviews})
          </h3>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recent">{t('productReviews.sortRecent')}</SelectItem>
              <SelectItem value="helpful">{t('productReviews.sortHelpful')}</SelectItem>
              <SelectItem value="rating_high">{t('productReviews.sortRatingHigh')}</SelectItem>
              <SelectItem value="rating_low">{t('productReviews.sortRatingLow')}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center py-12 text-gray-500">
            {t('productReviews.loading')}
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">{t('productReviews.noReviews')}</p>
            {isAuthenticated && (
              <Button
                onClick={() => setShowWriteReview(true)}
                className="bg-[#be1f1a] hover:bg-[#a71b17]"
              >
                {t('productReviews.beFirst')}
              </Button>
            )}
          </div>
        ) : (
          <div>
            {reviews.map((review) => (
              <ReviewCard
                key={review.id}
                review={review}
                onHelpful={handleHelpful}
              />
            ))}

            {totalPages > 1 && (
              <div className="flex justify-center gap-2 mt-6">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  {t('productReviews.previous')}
                </Button>
                <span className="flex items-center px-4 text-sm text-gray-600">
                  {t('productReviews.pageOf', { page, total: totalPages })}
                </span>
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  {t('productReviews.next')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Write Review Modal */}
      <WriteReview
        isOpen={showWriteReview}
        onClose={() => setShowWriteReview(false)}
        productId={productId}
        productName={productName}
        onSuccess={loadReviews}
      />
    </div>
  );
};

export default ProductReviews;
